// components/crm/LeadsManagement.jsx
import { ArrowDownTrayIcon, ArrowUpTrayIcon, CalendarIcon, ChatBubbleLeftEllipsisIcon, CheckIcon, FunnelIcon, MagnifyingGlassIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { downloadCsv, formatDateTimeForCsv } from '../../lib/csvExport';
import { api } from '../../lib/api';
import {
  LEAD_TABS,
  TERMINAL_STATUSES,
  getLeadStatusColor,
  getLeadStatusLabel,
  getStatusQueryParam,
} from '../../lib/leadTabs';
import AddLeadDialog from '../crm/dialogs/AddLeadDialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import ConvertToStudentDialog from './dialogs/ConvertToStudentDialog';
import ImportLeadsDialog from './dialogs/ImportLeadsDialog';
import LeadDetailsDialog from './dialogs/LeadDetailsDialog';

// ─── Quick Notes & Follow-up Dialog ───────────────────────────────────────────
function QuickNotesDialog({ lead, open, onClose, onUpdated, users = [] }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const textareaRef = useRef(null);

  // Load fresh lead data when dialog opens
  useEffect(() => {
    if (!open || !lead?._id) return;
    setLoadingNotes(true);
    api.get(`/leads/${lead._id}`)
      .then(r => {
        const l = r.data.data || r.data;
        setNotes(l.notes || []);
        setFollowUpDate(l.followUpDate ? l.followUpDate.slice(0, 10) : '');
        setAssignedTo(l.owner?._id || '');
      })
      .catch(() => toast.error('Failed to load lead details'))
      .finally(() => setLoadingNotes(false));
    setNewNote('');
  }, [open, lead?._id]);

  // Auto-focus textarea when dialog opens
  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 80);
  }, [open]);

  if (!open || !lead) return null;

  const handleAddNote = async () => {
    const text = newNote.trim();
    if (!text) return toast.error('Please enter a note');
    setSaving(true);
    try {
      const res = await api.post(`/leads/${lead._id}/notes`, { content: text });
      const updatedLead = res.data.data || res.data;
      setNotes(updatedLead.notes || []);
      setNewNote('');
      toast.success('Note added');
      onUpdated?.();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFollowUp = async () => {
    if (!followUpDate) return toast.error('Please select a follow-up date');
    setSaving(true);
    try {
      const payload = { followUpDate: followUpDate || null };
      if (assignedTo) payload.owner = assignedTo;
      await api.put(`/leads/${lead._id}`, payload);
      toast.success('Follow-up saved');
      onUpdated?.();
    } catch {
      toast.error('Failed to save follow-up');
    } finally {
      setSaving(false);
    }
  };

  const formatNoteDate = (d) =>
    new Date(d).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
              <ChatBubbleLeftEllipsisIcon className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-tight">{lead.name}</p>
              <p className="text-xs text-indigo-200 leading-tight">{lead.email || lead.phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors rounded-full p-1 hover:bg-white/10">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto popup-scroll p-5 space-y-5">

          {/* Follow-up Date + Assign To */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-amber-600" />
              <p className="text-sm font-semibold text-amber-800">Schedule Follow-up</p>
            </div>

            {/* Date row */}
            <div>
              <label className="text-xs font-medium text-amber-700 mb-1 block">Follow-up Date</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={followUpDate}
                  onChange={e => setFollowUpDate(e.target.value)}
                  className="flex-1 border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                />
                {followUpDate && (
                  <button
                    onClick={() => setFollowUpDate('')}
                    disabled={saving}
                    title="Clear date"
                    className="flex items-center px-2.5 py-2 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 bg-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Assign To row */}
            <div>
              <label className="text-xs font-medium text-amber-700 mb-1 block">Assign To</label>
              <select
                value={assignedTo}
                onChange={e => setAssignedTo(e.target.value)}
                className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">— Select staff —</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>{u.name} ({u.role})</option>
                ))}
              </select>
            </div>

            {/* Save button */}
            <button
              onClick={handleSaveFollowUp}
              disabled={saving}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {saving
                ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                : <CheckIcon className="h-4 w-4" />
              }
              Save Follow-up
            </button>
          </div>

          {/* Add Note */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <PencilSquareIcon className="h-4 w-4 text-indigo-600" />
              <p className="text-sm font-semibold text-indigo-800">Add Note</p>
            </div>
            <textarea
              ref={textareaRef}
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddNote(); }}
              placeholder="Write a note… (Ctrl+Enter to save)"
              rows={3}
              className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm resize-none bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={handleAddNote}
                disabled={saving || !newNote.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {saving
                  ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  : <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5" />
                }
                Add Note
              </button>
            </div>
          </div>

          {/* Notes history */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-[10px] font-bold text-gray-600">{notes.length}</span>
              </div>
              <p className="text-sm font-semibold text-gray-700">Notes History</p>
            </div>

            {loadingNotes ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-5 w-5 text-indigo-500" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <ChatBubbleLeftEllipsisIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No notes yet. Add your first note above.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {[...notes].reverse().map((note) => (
                  <div key={note._id} className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
                      <div className="flex items-center gap-1.5">
                        <div className="h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-[9px] font-bold text-indigo-600">
                            {(note.createdBy?.name || 'U')[0].toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 font-medium">
                          {note.createdBy?.name || 'User'}
                        </span>
                      </div>
                      <span className="text-[11px] text-gray-400">{formatNoteDate(note.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

/** Matches `.crm-table-badge*` in index.css — fixed min widths so Status/Priority/Source don’t jump */
const CRM_SOURCE_BADGE = 'crm-table-badge crm-table-badge--source';
const CRM_STATUS_BADGE = 'crm-table-badge crm-table-badge--status';
const CRM_PRIORITY_BADGE = 'crm-table-badge crm-table-badge--priority';
const CRM_TERMINAL_BADGE = 'crm-table-badge crm-table-badge--status';

// ─── Dashboard-style skeleton animations ──────────────────────────────────────
const CrmSkeletonStyles = () => (
  <style>{`
    @keyframes crm-shimmer {
      0%,100% { background-position: 200% 0; }
      50%      { background-position: -200% 0; }
    }
    @keyframes crm-shimmer-slow {
      0%,100% { background-position: 200% 0; }
      50%      { background-position: -200% 0; }
    }
    @keyframes crm-pulse-scale {
      0%,100% { transform: scale(1);    opacity: 0.8; }
      50%      { transform: scale(1.04); opacity: 1;   }
    }
    @keyframes crm-float {
      0%,100% { transform: translateY(0px);  }
      50%      { transform: translateY(-6px); }
    }
    .crm-shimmer      { animation: crm-shimmer 2s ease-in-out infinite; background-size: 200% 100%; }
    .crm-shimmer-slow { animation: crm-shimmer-slow 3s ease-in-out infinite; background-size: 200% 100%; }
    .crm-pulse        { animation: crm-pulse-scale 2s ease-in-out infinite; }
    .crm-float        { animation: crm-float 3s ease-in-out infinite; }
    .crm-d75  { animation-delay: 75ms;  }
    .crm-d150 { animation-delay: 150ms; }
    .crm-d300 { animation-delay: 300ms; }
  `}</style>
);

// ─── Dashboard-style row skeleton ─────────────────────────────────────────────
const RowSkeleton = ({ index = 0 }) => (
  <TableRow className="border-b border-gray-100 relative overflow-hidden">
    {/* Full-row shimmer sweep */}
    <td className="absolute inset-0 pointer-events-none">
      <div className="w-full h-full bg-gradient-to-r from-transparent via-white/50 to-transparent crm-shimmer-slow" />
    </td>
    {/* # */}
    <TableCell className="py-3 w-10">
      <div className="h-3 w-5 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 crm-shimmer" />
    </TableCell>
    {/* Name / email / phone */}
    <TableCell className="py-3">
      <div className="space-y-1.5">
        <div className="h-3 w-28 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 crm-shimmer" style={{ animationDelay: `${index * 40}ms` }} />
        <div className="h-2.5 w-36 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 crm-shimmer crm-d75" />
        <div className="h-2.5 w-24 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 crm-shimmer crm-d150" />
      </div>
    </TableCell>
    {/* Course */}
    <TableCell className="py-3">
      <div className="h-3 w-24 rounded-full bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 crm-pulse" />
    </TableCell>
    {/* Source badge */}
    <TableCell className="py-3">
      <div className="h-5 w-16 rounded-full bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 crm-shimmer" />
    </TableCell>
    {/* Status badge */}
    <TableCell className="py-3">
      <div className="h-5 w-20 rounded-full bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 crm-pulse crm-d75" />
    </TableCell>
    {/* Priority */}
    <TableCell className="py-3">
      <div className="h-5 w-14 rounded-full bg-gradient-to-r from-orange-100 via-orange-200 to-orange-100 crm-pulse crm-d150" />
    </TableCell>
    {/* Owner */}
    <TableCell className="py-3">
      <div className="h-3 w-20 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 crm-shimmer" />
    </TableCell>
    {/* Date */}
    <TableCell className="py-3">
      <div className="h-3 w-20 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 crm-shimmer crm-d75" />
    </TableCell>
    {/* Convert btn */}
    <TableCell className="py-3">
      <div className="h-6 w-14 rounded-md bg-gradient-to-r from-green-100 via-green-200 to-green-100 crm-pulse crm-float" />
    </TableCell>
    {/* Notes btn */}
    <TableCell className="py-3 text-center">
      <div className="h-6 w-12 rounded-md bg-gradient-to-r from-indigo-100 via-indigo-200 to-indigo-100 crm-shimmer mx-auto" />
    </TableCell>
    {/* View btn */}
    <TableCell className="py-3 text-center">
      <div className="h-6 w-12 rounded-md bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 crm-shimmer mx-auto" />
    </TableCell>
  </TableRow>
);

const getPriorityColor = (priority) => {
  const colors = { high: 'bg-red-100 text-red-800', medium: 'bg-yellow-100 text-yellow-800', low: 'bg-green-100 text-green-800' };
  return colors[priority] || 'bg-gray-100 text-gray-800';
};

const formatDateTime = (dateString) =>
  new Date(dateString).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

export default function LeadsManagement({ onDataUpdate }) {
  const [searchParams] = useSearchParams();

  // ── Active tab from URL (?tab=new) ───────────────────────────────────────────
  const activeLeadTab = searchParams.get('tab') || '';

  // ── Data state ───────────────────────────────────────────────────────────────
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportingCsv, setExportingCsv] = useState(false);

  // ── DRAFT filter state (edited inside the filter panel, NOT sent to API) ─────
  const BLANK_DRAFT = {
    nameSearch: '', emailSearch: '', phoneSearch: '',
    statusFilter: 'all', priorityFilter: 'all',
    ownerFilter: 'all', sourceFilter: 'all', courseFilter: 'all',
    followUpFrom: '', followUpTo: '',
  };
  const [draft, setDraft] = useState(BLANK_DRAFT);

  // ── APPLIED filter state (sent to API — only updated when Apply is clicked) ──
  const [applied, setApplied] = useState(BLANK_DRAFT);

  // ── Dialog / selection state ─────────────────────────────────────────────────
  const [selectedLead, setSelectedLead] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);

  // ── Reference data ────────────────────────────────────────────────────────────
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // ── Core fetch — uses explicit params, never reads component state ────────────
  const fetchLeads = useCallback(async (filters = applied, targetPage = page, tab = activeLeadTab) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      const search = (filters.nameSearch || filters.emailSearch || filters.phoneSearch || '').trim();
      const tabStatus = tab ? getStatusQueryParam(tab) : 'all';
      const statusParam = filters.statusFilter !== 'all' ? filters.statusFilter : tabStatus;

      params.set('page', targetPage);
      params.set('limit', PAGE_SIZE);
      params.set('sortBy', 'createdAt');
      params.set('sortOrder', 'desc');

      if (statusParam && statusParam !== 'all') params.set('status', statusParam);
      if (search) params.set('search', search);
      if (filters.priorityFilter !== 'all') params.set('priority', filters.priorityFilter);
      if (filters.ownerFilter !== 'all') params.set('owner', filters.ownerFilter);
      if (filters.sourceFilter !== 'all') params.set('source', filters.sourceFilter);
      if (filters.courseFilter !== 'all') params.set('interestCourse', filters.courseFilter);
      if (filters.followUpFrom) params.set('followUpFrom', filters.followUpFrom);
      if (filters.followUpTo) params.set('followUpTo', filters.followUpTo);

      const res = await api.get(`/leads?${params}`);
      setLeads(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
      setPages(res.data.pagination?.pages || 1);
    } catch (err) {
      console.error('Error fetching leads:', err);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, []); // no filter deps — avoids auto-firing on every state change

  // ── Reference data loaders ────────────────────────────────────────────────────
  const fetchCourses = useCallback(async () => {
    try {
      setCoursesLoading(true);
      // Use student-dashboard endpoint — returns active courses with fee info, no auth issues
      const res = await api.get('/student-dashboard/courses');
      setCourses(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/auth/staff-list');
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  // ── Initial load (once) ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchLeads(BLANK_DRAFT, 1, activeLeadTab);
    fetchCourses();
    fetchUsers();
  }, []); // intentional: run once on mount only

  // ── Re-fetch when URL tab param changes ───────────────────────────────────────
  useEffect(() => {
    // Reset applied filters when the tab changes, keep the draft as-is
    const resetApplied = { ...BLANK_DRAFT };
    setApplied(resetApplied);
    setPage(1);
    fetchLeads(resetApplied, 1, activeLeadTab);
  }, [activeLeadTab]); // intentional: fetchLeads is stable (empty dep array)

  // ── Apply filters (called only when user clicks "Apply Filters") ──────────────
  const handleApplyFilters = useCallback(() => {
    setApplied(draft);
    setPage(1);
    fetchLeads(draft, 1, activeLeadTab);
    setFilterOpen(false);
  }, [draft, activeLeadTab, fetchLeads]);

  // ── Clear all filters ─────────────────────────────────────────────────────────
  const handleClearSearch = useCallback(() => {
    setDraft(BLANK_DRAFT);
    setApplied(BLANK_DRAFT);
    setPage(1);
    fetchLeads(BLANK_DRAFT, 1, activeLeadTab);
    setFilterOpen(false);
  }, [activeLeadTab, fetchLeads]);

  // Alias kept for Enter-key on search inputs
  const handleGenerateSearch = handleApplyFilters;

  // ── Pagination ────────────────────────────────────────────────────────────────
  const handlePage = (next) => {
    setPage(next);
    fetchLeads(applied, next, activeLeadTab);
  };

  // ── Post-mutation refresh ─────────────────────────────────────────────────────
  const refreshAll = useCallback(() => {
    fetchLeads(applied, page, activeLeadTab);
    if (onDataUpdate) onDataUpdate();
  }, [applied, page, activeLeadTab, fetchLeads, onDataUpdate]);

  // ── Convenience draft helpers ─────────────────────────────────────────────────
  const setD = (key) => (val) => setDraft(p => ({ ...p, [key]: val }));
  const setDE = (key) => (e) => setDraft(p => ({ ...p, [key]: e.target.value }));

  const handleLeadAdded = useCallback(() => { refreshAll(); setIsAddDialogOpen(false); }, [refreshAll]);
  const handleLeadsImported = useCallback(() => { refreshAll(); setIsImportDialogOpen(false); }, [refreshAll]);
  const handleLeadUpdated = useCallback(() => { refreshAll(); }, [refreshAll]);
  const handleConvertSuccess = useCallback(() => { refreshAll(); setIsConvertDialogOpen(false); setSelectedLead(null); }, [refreshAll]);

  const handleExportLeadsCsv = useCallback(async () => {
    try {
      setExportingCsv(true);
      const params = new URLSearchParams();
      params.set('limit', '5000');
      if (exportFromDate) params.set('createdAtFrom', exportFromDate);
      if (exportToDate) params.set('createdAtTo', exportToDate);
      const res = await api.get(`/leads?${params}`);
      const data = res.data.data || [];
      const headers = [
        'Name', 'Email', 'Phone', 'Course', 'Batch', 'Batch Timings',
        'Qualification', 'Year Passed Out', 'Preferred Mode',
        'Source', 'Reference Person',
        'Status', 'Priority', 'Owner', 'Follow-up Date', 'Created At',
      ];
      const rows = data.map((l) => [
        l.name || '-',
        l.email || '-',
        l.phone || '-',
        l.interestCourse?.name || '-',
        l.interestBatch?.name || '-',
        l.batchTimings || '-',
        l.qualification || '-',
        l.yearOfPassedOut || '-',
        l.preferredMode || '-',
        l.source || '-',
        l.referencePersonName || '-',
        l.status || '-',
        l.priority || '-',
        l.owner?.name || '-',
        l.followUpDate ? formatDateTimeForCsv(l.followUpDate) : '-',
        formatDateTimeForCsv(l.createdAt),
      ]);
      downloadCsv(headers, rows, `leads-export-${exportFromDate || 'all'}-${exportToDate || 'all'}.csv`);
      toast.success(`Exported ${rows.length} leads`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to export leads');
    } finally {
      setExportingCsv(false);
    }
  }, [exportFromDate, exportToDate]);

  // Count of currently applied (committed) filters — drives the badge on the button
  const appliedCount = [
    applied.nameSearch, applied.emailSearch, applied.phoneSearch,
    applied.sourceFilter !== 'all',
    applied.courseFilter !== 'all',
    applied.statusFilter !== 'all',
    applied.priorityFilter !== 'all',
    applied.ownerFilter !== 'all',
    applied.followUpFrom,
    applied.followUpTo,
  ].filter(Boolean).length;

  // The current tab's statuses — used to build the fine-grained status dropdown
  const currentTabStatuses = LEAD_TABS.find(t => t.value === activeLeadTab)?.statuses ?? [];

  return (
    <div className="space-y-4">
      <CrmSkeletonStyles />
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold">Leads Management</h2>
          <p className="text-sm text-gray-600">Manage and track your leads</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Filter button with active (applied) count badge */}
          <button
            onClick={() => setFilterOpen(true)}
            className="relative flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-blue-300 transition-all text-sm font-medium text-gray-700 shadow-sm"
          >
            <FunnelIcon className="h-4 w-4 text-blue-500" />
            Search &amp; Filters
            {appliedCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                {appliedCount}
              </span>
            )}
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={exportFromDate} onChange={(e) => setExportFromDate(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
            <input type="date" value={exportToDate} onChange={(e) => setExportToDate(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
            <Button variant="outline" size="sm" onClick={handleExportLeadsCsv} disabled={exportingCsv} className="flex items-center gap-1">
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exportingCsv ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
          <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            Import
          </Button>
          <ImportLeadsDialog
            open={isImportDialogOpen}
            onOpenChange={setIsImportDialogOpen}
            onSuccess={handleLeadsImported}
          />
          <AddLeadDialog
            open={isAddDialogOpen}
            onOpenChange={setIsAddDialogOpen}
            courses={courses}
            coursesLoading={coursesLoading}
            onSuccess={handleLeadAdded}
          />
        </div>
      </div>

      {/* ── Filter Modal ───────────────────────────────────────────────────── */}
      {filterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setFilterOpen(false)} />

          {/* Panel */}
          <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-blue-700">
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-5 w-5 text-white" />
                <span className="text-base font-semibold text-white">Search &amp; Filters</span>
              </div>
              <button onClick={() => setFilterOpen(false)} className="text-white/80 hover:text-white transition-colors">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto popup-scroll">
              {/* Tab bar */}
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                {['Basic', 'Advanced'].map((tab, i) => (
                  <button
                    key={tab}
                    onClick={() => setShowAdvanced(i === 1)}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${showAdvanced === (i === 1)
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {!showAdvanced ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Contact Name</label>
                    <Input placeholder="Search by name…" value={draft.nameSearch} onChange={setDE('nameSearch')} onKeyDown={e => e.key === 'Enter' && handleApplyFilters()} className="h-9 text-sm" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Email Address</label>
                    <Input placeholder="Search by email…" value={draft.emailSearch} onChange={setDE('emailSearch')} onKeyDown={e => e.key === 'Enter' && handleApplyFilters()} className="h-9 text-sm" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Mobile Number</label>
                    <Input placeholder="Search by phone…" value={draft.phoneSearch} onChange={setDE('phoneSearch')} onKeyDown={e => e.key === 'Enter' && handleApplyFilters()} className="h-9 text-sm" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Lead Owner</label>
                    <Select value={draft.ownerFilter} onValueChange={setD('ownerFilter')}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All Owners" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Owners</SelectItem>
                        {users.map(u => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Lead Stage</label>
                    <Select value={draft.statusFilter} onValueChange={setD('statusFilter')}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All Stages" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Stages</SelectItem>
                        {currentTabStatuses.map(s => <SelectItem key={s} value={s} className="capitalize">{getLeadStatusLabel(s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Interest Course</label>
                    <Select value={draft.courseFilter} onValueChange={setD('courseFilter')}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All Courses" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Courses</SelectItem>
                        {courses.map(c => <SelectItem key={c._id} value={String(c._id)}>{c.courseName || c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Lead Source</label>
                    <Select value={draft.sourceFilter} onValueChange={setD('sourceFilter')}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All Sources" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        <SelectItem value="phone_enquiry">Phone Enquiry</SelectItem>
                        <SelectItem value="website">Website / Google</SelectItem>
                        <SelectItem value="social">Social Media</SelectItem>
                        <SelectItem value="reference">Friend / Reference</SelectItem>
                        <SelectItem value="walkin">Walk-in</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Priority</label>
                    <Select value={draft.priorityFilter} onValueChange={setD('priorityFilter')}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All Priorities" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Follow-up From</label>
                    <Input type="date" value={draft.followUpFrom} onChange={setDE('followUpFrom')} className="h-9 text-sm" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Follow-up To</label>
                    <Input type="date" value={draft.followUpTo} onChange={setDE('followUpTo')} className="h-9 text-sm" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Lead Stage</label>
                    <Select value={draft.statusFilter} onValueChange={setD('statusFilter')}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All Stages" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Stages</SelectItem>
                        {currentTabStatuses.map(s => <SelectItem key={s} value={s} className="capitalize">{getLeadStatusLabel(s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Priority</label>
                    <Select value={draft.priorityFilter} onValueChange={setD('priorityFilter')}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All Priorities" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50">
              <Button size="sm" variant="outline" onClick={handleClearSearch} className="text-sm gap-1.5">
                ↺ Clear All
              </Button>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setDraft(applied); setFilterOpen(false); }} className="text-sm">
                  Cancel
                </Button>
                <Button size="sm" onClick={handleApplyFilters} disabled={loading}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white gap-1.5 px-5">
                  {loading
                    ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    : <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                  }
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <CardTitle>{LEAD_TABS.find(t => t.value === activeLeadTab)?.label ?? 'Leads'}</CardTitle>
          <CardDescription>
            {loading ? 'Loading…' : `${total} lead${total !== 1 ? 's' : ''} found`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-blue-500 hover:bg-blue-500">
                  <TableHead className="text-white font-semibold text-xs w-10">#</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Lead</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Course</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Source</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Status</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Priority</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Owner</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Created At</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Convert</TableHead>
                  <TableHead className="text-center text-white font-semibold text-xs">Notes</TableHead>
                  <TableHead className="text-center text-white font-semibold text-xs">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} index={i} />)
                ) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-10 text-gray-500 text-sm">
                      No leads found for this filter
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead, index) => {
                    const isTerminal = TERMINAL_STATUSES.has(lead.status);
                    const serialNo = (page - 1) * PAGE_SIZE + index + 1;
                    return (
                      <TableRow key={lead._id} className="hover:bg-gray-50">
                        <TableCell className="text-xs text-gray-500 font-medium text-center">
                          {serialNo}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-xs">{lead.name}</p>
                            <p className="text-xs text-gray-500">{lead.email}</p>
                            <p className="text-xs text-gray-500">{lead.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-xs">{lead.interestCourse?.name || 'N/A'}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${CRM_SOURCE_BADGE} font-normal`}>
                            {lead.source}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${CRM_STATUS_BADGE} font-normal ${getLeadStatusColor(lead.status)}`}>
                            {getLeadStatusLabel(lead.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${CRM_PRIORITY_BADGE} font-normal ${getPriorityColor(lead.priority)}`}>
                            {lead.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-xs">{lead.owner?.name || 'System'}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                            <CalendarIcon className="h-3 w-3 flex-shrink-0" />
                            {lead.createdAt
                              ? new Date(lead.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                              : '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* Convert button hidden for all terminal statuses */}
                          {isTerminal ? (
                            <Badge className={`${CRM_TERMINAL_BADGE} bg-gray-100 font-normal text-gray-500`}>
                              {getLeadStatusLabel(lead.status)}
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => { setSelectedLead(lead); setIsConvertDialogOpen(true); }}
                              className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                            >
                              Convert
                            </Button>
                          )}
                        </TableCell>
                        {/* Notes & Follow-up */}
                        <TableCell className="text-center align-middle">
                          <button
                            onClick={() => { setSelectedLead(lead); setIsNotesDialogOpen(true); }}
                            title="Notes & Follow-up"
                            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-medium bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 hover:border-indigo-400 transition-colors"
                          >
                            <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5" />
                            {lead.notes?.length > 0 && (
                              <span className="bg-indigo-600 text-white rounded-full h-4 w-4 flex items-center justify-center text-[10px] font-bold">
                                {lead.notes.length}
                              </span>
                            )}
                          </button>
                        </TableCell>

                        {/* View */}
                        <TableCell className="text-center align-middle">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedLead(lead); setIsViewDialogOpen(true); }}
                            className="h-7 px-3 text-xs"
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t">
              <p className="text-xs text-gray-500">
                Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-1.5">
                {/* Jump to first page */}
                <Button size="sm" variant="outline" disabled={page <= 1 || loading}
                  onClick={() => handlePage(1)} className="text-xs h-7">
                  « First
                </Button>
                <Button size="sm" variant="outline" disabled={page <= 1 || loading}
                  onClick={() => handlePage(page - 1)} className="text-xs h-7">
                  ← Prev
                </Button>

                {/* Leading ellipsis if current window doesn't start at 1 */}
                {page > 3 && pages > 5 && (
                  <span className="text-xs text-gray-400 px-1">…</span>
                )}

                {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
                  const p = pages <= 5 ? i + 1
                    : page <= 3 ? i + 1
                      : page >= pages - 2 ? pages - 4 + i
                        : page - 2 + i;
                  return (
                    <Button key={p} size="sm"
                      variant={p === page ? 'default' : 'outline'}
                      onClick={() => handlePage(p)}
                      className={`text-xs h-7 w-7 ${p === page ? 'bg-blue-600 text-white' : ''}`}>
                      {p}
                    </Button>
                  );
                })}

                {/* Trailing ellipsis if current window doesn't reach the last page */}
                {page < pages - 2 && pages > 5 && (
                  <span className="text-xs text-gray-400 px-1">…</span>
                )}

                <Button size="sm" variant="outline" disabled={page >= pages || loading}
                  onClick={() => handlePage(page + 1)} className="text-xs h-7">
                  Next →
                </Button>
                {/* Jump to last page */}
                <Button size="sm" variant="outline" disabled={page >= pages || loading}
                  onClick={() => handlePage(pages)} className="text-xs h-7">
                  Last »
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lead Details Dialog */}
      <LeadDetailsDialog
        lead={selectedLead}
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        courses={courses}
        coursesLoading={coursesLoading}
        users={users}
        onLeadUpdated={handleLeadUpdated}
      />

      {/* Convert to Student Dialog */}
      <ConvertToStudentDialog
        lead={selectedLead}
        open={isConvertDialogOpen}
        onOpenChange={setIsConvertDialogOpen}
        onSuccess={handleConvertSuccess}
      />

      {/* Quick Notes & Follow-up Dialog */}
      <QuickNotesDialog
        lead={selectedLead}
        open={isNotesDialogOpen}
        onClose={() => { setIsNotesDialogOpen(false); setSelectedLead(null); }}
        onUpdated={refreshAll}
        users={users}
      />
    </div>
  );
}